import React, { forwardRef } from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, LinkProps } from "react-router-dom";
import isPropValid from "@emotion/is-prop-valid";
import { WithTheme } from "../../../styles/theme";
import { isLight, lightenDarkenColor } from "../../../styles/colorConvertor";
import { css } from "../../../utils/ide";
import { faPlus } from "@fortawesome/pro-light-svg-icons";

export type IOButtonVisualProps = {
  cancel?: boolean;
  outline?: boolean;
  circle?: boolean;
  fit?: boolean;
  smallPadding?: boolean;
  noPadding?: boolean;
  add?: boolean;
  error?: boolean;
  warning?: boolean;
  processing?: boolean;
  success?: boolean;
  standard?: boolean;
  outlinePrimary?: boolean;
  whiteBg?: boolean;
  color?: string;
  rounded?: boolean;
  warningDark?: boolean;
  info?: boolean;
  gray100?: boolean;
  bgPrimary?: boolean;
  successDark?: boolean;
  textColorCircle?: boolean;
  hidden?: boolean;
};

export type IOButtonProps = IOButtonVisualProps & {
  cy?: string;
  to?: string;
  href?: string;
  target?: string;
  replace?: boolean;
  icon?: any;
  expandable?: boolean;
  expandableIcon?: any;
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type ButtonStyleProps = IOButtonVisualProps;

const buttonCss = ({
  cancel,
  outline,
  circle,
  fit,
  smallPadding,
  noPadding,
  add,
  error,
  warning,
  processing,
  success,
  standard,
  outlinePrimary,
  whiteBg,
  color,
  rounded,
  warningDark,
  gray100,
  bgPrimary,
  successDark,
  info,
  hidden,
  theme,
}: WithTheme<ButtonStyleProps>) => css`
  border: ${outline
    ? `1px solid ${
        bgPrimary
          ? theme.textOnPrimary
          : outline
          ? theme.type === "dark"
            ? theme.gray.gray700
            : theme.gray.gray300
          : theme.text.secondary
      }`
    : "none"};
  box-sizing: border-box;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  outline: none;
  ${!hidden &&
  css`
    display: inline-flex;
  `}
  ${fit ? "width: 100%;" : ""}
  justify-content: center;
  align-items: center;
  padding: 8px ${noPadding ? 0 : smallPadding ? 20 : add && circle ? 10 : 40}px;
  color: ${circle || cancel
    ? theme.text.secondary || theme.background.secondary
    : outline
    ? bgPrimary
      ? theme.textOnPrimary
      : theme.text.secondary
    : color
    ? isLight(color)
      ? "black"
      : "white"
    : "white"};
  background: ${standard
    ? theme.notification.infoMain
    : success
    ? theme.notification.successMain
    : successDark
    ? theme.notification.successDark
    : warning
    ? theme.notification.warningMain
    : circle || outline
    ? "none"
    : cancel
    ? theme.type === "light"
      ? "rgba(0,0,0,0.05)"
      : "rgba(255,255,255,0.05)"
    : error
    ? theme.notification.errorMain
    : color
    ? color
    : info
    ? theme.notification.infoMain
    : theme.primary || theme.background.secondary};
  &:hover {
    background: ${success
      ? lightenDarkenColor(theme.notification.successMain, 10)
      : circle || outline || outlinePrimary
      ? "none"
      : cancel
      ? "rgba(0,0,0,0.1)"
      : lightenDarkenColor(theme.text.secondary, 5)};
    border-color: ${circle || outline
      ? lightenDarkenColor(theme.text.secondary, 25)
      : "none"};
    color: ${circle ||
    outline ||
    outlinePrimary ||
    (cancel && theme.type === "light")
      ? lightenDarkenColor(theme.text.secondary, 25)
      : "white"};
  }
  &:disabled {
    opacity: 0.3;
    background: ${processing
      ? lightenDarkenColor(theme.primary, 25)
      : theme.type === "dark" && !whiteBg
      ? "rgba(255, 255, 255, 0.15)"
      : theme.gray.gray400};
    ${theme.type === "dark" && !whiteBg
      ? `
          color: ${theme.gray.gray500};
        `
      : ""}
    cursor: not-allowed;
  }
  svg {
    position: relative;
  }

  ${outlinePrimary &&
  css`
    color: ${theme.primary};
    background: none;
    border: 1px solid ${theme.primary};
  `}

  ${rounded &&
  css`
    border-radius: 15px;
  `};

  ${warningDark &&
  css`
    background: ${theme.notification.warningDark};
    &:hover {
      background: ${lightenDarkenColor(theme.notification.warningDark, 25)};
    }
  `};

  ${gray100 &&
  css`
    background: ${theme.gray.gray100};
    &:hover {
      background: ${lightenDarkenColor(theme.gray.gray100, 5)};
      color: black;
    }
    color: black;
  `}
`;

const ButtonContainer = styled.button<ButtonStyleProps>(buttonCss);

const ButtonLink = styled(Link as any, {
  shouldForwardProp(propName: string) {
    return (
      isPropValid(propName) ||
      ["component", "to", "replace", "innerRef"].includes(propName)
    );
  },
})<ButtonStyleProps & LinkProps>(buttonCss);

const ButtonExternalLink = styled.a<ButtonStyleProps>(buttonCss);

interface IconContainerProps {
  circle?: boolean;
  textColorCircle?: boolean;
  info?: boolean;
  marginLeft?: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  ${(props) =>
    !props.hidden &&
    css`
      display: flex;
    `}
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  height: ${(props) => (props.circle ? 24 : 12)}px;
  width: ${(props) => (props.circle ? 24 : 12)}px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid
    ${({ theme, circle, textColorCircle, info }) =>
      info
        ? theme.notification.infoMain
        : circle
        ? textColorCircle
          ? theme.text.secondary
          : theme.primary
        : "none"};
  path {
    fill: ${({ theme, textColorCircle, info }) =>
      info
        ? theme.notification.infoMain
        : textColorCircle
        ? theme.text.secondary
        : theme.primary};
  }
  margin-left: ${(props) => (props.marginLeft ? 10 : 0)}px;
`;

const IOButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IOButtonProps
>((props, ref) => {
  const { cy, to, href, target, replace, ...rest } = props;
  const ButtonComponent: React.ComponentType<any> = to
    ? ButtonLink
    : href
    ? ButtonExternalLink
    : ButtonContainer;
  const buttonProps =
    to || href
      ? ({ ...rest, to, href, target, replace } as ButtonStyleProps & LinkProps)
      : rest;

  return (
    <ButtonComponent ref={ref as React.Ref<any>} data-cy={cy} {...buttonProps}>
      {props.icon && (
        <FontAwesomeIcon style={{ marginRight: 8 }} icon={props.icon} />
      )}
      {props.processing ? (
        <div style={{ marginRight: 8 }} data-cy="button-processing-spinner">
          <FontAwesomeIcon icon={["fas", "spinner"]} pulse />
        </div>
      ) : null}
      {props.add ? (
        <IconContainer
          info={props.info}
          textColorCircle={props.textColorCircle}
          circle={props.circle}
        >
          <FontAwesomeIcon icon={faPlus} />
        </IconContainer>
      ) : null}
      <span>{props.children}</span>
      {props.expandable && props.expandableIcon ? (
        <IconContainer
          textColorCircle={props.textColorCircle}
          circle={props.circle}
          marginLeft
        >
          <FontAwesomeIcon icon={["fas", props.expandableIcon]} />
        </IconContainer>
      ) : null}
    </ButtonComponent>
  );
});

IOButton.displayName = "IOButton";

export default IOButton;
